import { alternatesToLinks, buildCanonicalAlternates } from "@/i18n/alternates";

export type CatalogSeoEntityType = "work" | "release" | "artist" | "company";
export type CatalogSeoDirective = "index" | "canonical" | "noindex";

export interface CatalogEntitySeoPolicy {
  directive: CatalogSeoDirective;
  sitemapEligible: boolean;
  sitemapCohort: number;
  canonicalEntityType: CatalogSeoEntityType;
  canonicalCatalogId: string;
  qualityTier: number;
  indexLocales: string[];
  contentFingerprint: string;
}

interface ResolveCatalogSeoInput {
  policy: CatalogEntitySeoPolicy | null | undefined;
  currentEntityType: CatalogSeoEntityType;
  currentCatalogId: string;
  locale: string;
}

function entityPath(entityType: CatalogSeoEntityType, catalogId: string): string {
  return `/${entityType}/${encodeURIComponent(catalogId)}`;
}

/** Maps one pressed policy row to canonical, robots, hreflang and JSON-LD behavior. */
export function resolveCatalogEntitySeo({
  policy,
  currentEntityType,
  currentCatalogId,
}: ResolveCatalogSeoInput) {
  const effectivePolicy: CatalogEntitySeoPolicy = policy ?? {
    directive: "index",
    sitemapEligible: false,
    sitemapCohort: 4,
    canonicalEntityType: currentEntityType,
    canonicalCatalogId: currentCatalogId,
    qualityTier: 9,
    indexLocales: ["pt"],
    contentFingerprint: "",
  };
  const targetPath = entityPath(
    effectivePolicy.canonicalEntityType,
    effectivePolicy.canonicalCatalogId,
  );
  const selfPath = entityPath(currentEntityType, currentCatalogId);
  const indexCurrentPage = effectivePolicy.directive === "index";
  const canonicalPath = effectivePolicy.directive === "noindex" ? selfPath : targetPath;
  const alternates = buildCanonicalAlternates(canonicalPath);

  return {
    canonicalUrl: alternates.canonical,
    links: alternatesToLinks(alternates),
    robots: effectivePolicy.directive === "noindex" ? "noindex,follow" : null,
    emitStructuredData: indexCurrentPage,
  } as const;
}
